import { useEffect, useState } from 'react';
import type { SetURLSearchParams } from 'react-router-dom';

import type { ModuleViewMode } from './project-module-table.tsx';

export const DEFAULT_MODULE_VIEW_MODE: ModuleViewMode = 'table';
export const MODULE_VIEW_STORAGE_KEY = 'youkuai.project-module-layout';

export function parseModuleViewMode(value: string | null | undefined): ModuleViewMode | null {
  return value === 'split' || value === 'table' ? value : null;
}

export function readStoredModuleViewMode(storage?: Pick<Storage, 'getItem'> | null): ModuleViewMode | null {
  try {
    return parseModuleViewMode(storage?.getItem(MODULE_VIEW_STORAGE_KEY));
  } catch {
    return null;
  }
}

export function resolveModuleViewMode(urlValue: string | null | undefined,
  stored: ModuleViewMode | null): ModuleViewMode {
  return parseModuleViewMode(urlValue) ?? stored ?? DEFAULT_MODULE_VIEW_MODE;
}

export function writeStoredModuleViewMode(storage: Pick<Storage, 'setItem'> | null | undefined,
  mode: ModuleViewMode): void {
  try {
    storage?.setItem(MODULE_VIEW_STORAGE_KEY, mode);
  } catch {
    // 存储不可用时仍允许本次页面切换。
  }
}

/** `layout` 专用于模块布局，避免与工作/报销的业务 `view` 参数冲突。 */
export function withModuleViewMode(params: URLSearchParams, mode: ModuleViewMode): URLSearchParams {
  const next = new URLSearchParams(params);
  if (parseModuleViewMode(next.get('view'))) next.delete('view');
  if (mode === DEFAULT_MODULE_VIEW_MODE) next.delete('layout');
  else next.set('layout', mode);
  return next;
}

function modeFromParams(params: URLSearchParams): string | null {
  return params.get('layout') ?? (parseModuleViewMode(params.get('view')) ? params.get('view') : null);
}

export function useModuleViewMode(params: URLSearchParams, setParams: SetURLSearchParams):
  readonly [ModuleViewMode, (mode: ModuleViewMode) => void] {
  const urlMode = modeFromParams(params);
  const [mode, setMode] = useState<ModuleViewMode>(() => resolveModuleViewMode(urlMode,
    readStoredModuleViewMode(typeof window === 'undefined' ? null : window.localStorage)));

  useEffect(() => {
    const next = resolveModuleViewMode(urlMode,
      readStoredModuleViewMode(typeof window === 'undefined' ? null : window.localStorage));
    setMode((current) => current === next ? current : next);
  }, [urlMode]);

  function changeMode(next: ModuleViewMode) {
    setMode(next);
    writeStoredModuleViewMode(typeof window === 'undefined' ? null : window.localStorage, next);
    setParams(withModuleViewMode(params, next), { replace: true });
  }

  return [mode, changeMode] as const;
}
